<template>
  <div class="tw-relative type-spin-viewer" @mouseenter="startPreload" @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerUp" @pointercancel="onPointerUp" @dragstart.prevent @wheel="onWheel">
    <template v-if="hasViewer">
      <div class="type-spin-ratio" :class="{ 'type-spin-ratio--lcv': isLcv }">
        <!-- Turntable rotation hint = two curved arrows (vector copied from the reference site's "3D arrow L/R" Imagin turntable). z-index:0 → sits behind the car; center gap is where the car stands. -->
        <svg class="spin-rotate-hint" viewBox="0 0 900 56" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false">
          <g transform="translate(38,0)">
            <g transform="translate(548,27.5) scale(1,-1) translate(-548,-27.5)">
              <g transform="translate(0,0.7259)">
                <path fill="#707070" d="M162.4879,53.995 C139.2679,52.394 118.0549,50.65 99.2449,48.786 C80.4259,46.922 64.0189,44.938 50.3029,42.866 C22.8349,38.722 6.2389,34.223 1.4689,29.659 C-0.9251,27.377 -0.3941,25.078 3.0709,22.799 C6.5359,20.521 12.9709,18.263 22.2409,16.062 C39.9799,11.842 68.3649,7.837 105.6949,4.28 L111.9169,4.362 L113.9659,4.776 C76.8969,8.215 48.3679,12.106 30.1339,16.227 C20.5669,18.386 13.7719,20.607 9.9019,22.853 C6.0229,25.1 5.0329,27.372 6.9589,29.634 C10.7659,34.157 26.3089,38.637 52.8049,42.787 C66.0349,44.862 81.9739,46.854 100.3699,48.731 C118.7569,50.609 139.5919,52.371 162.4879,53.995"/>
                <polygon fill="#707070" points="127.5769 7.5234 105.6949 4.2804 162.4879 0.2744"/>
              </g>
            </g>
          </g>
          <g transform="translate(701,0)">
            <g transform="translate(-384,27.5) scale(1,-1) translate(384,-27.5) translate(-932,0)">
              <g transform="translate(932,0.7259)">
                <path fill="#707070" d="M0.6809,54 C23.5769,52.377 44.4119,50.614 62.7989,48.736 C81.1859,46.859 97.1339,44.867 110.3639,42.792 C136.8599,38.643 152.4029,34.163 156.2099,29.639 C158.1269,27.377 157.1459,25.105 153.2669,22.859 C149.3969,20.612 142.5929,18.391 133.0349,16.233 C114.8009,12.111 86.2739,8.22 49.2039,4.781 L51.2519,4.367 L57.4739,4.286 C94.8039,7.842 123.1799,11.847 140.9189,16.067 C150.1889,18.268 156.6329,20.526 160.0889,22.805 C163.5629,25.083 164.0939,27.382 161.6909,29.664 C156.9299,34.229 140.3339,38.727 112.8659,42.871 C99.1499,44.943 82.7429,46.927 63.9239,48.791 C45.1139,50.656 23.9009,52.4 0.6809,54"/>
                <polygon fill="#707070" points="0.6809 0.2797 35.5919 7.5287 57.4739 4.2857"/>
              </g>
            </g>
          </g>
        </svg>
        <img
          v-for="(item, i) in validAngles"
          :key="item.angle"
          v-show="i === currentIndex"
          class="type-spin-img"
          draggable="false"
          :style="{ transform: 'scale(' + zoom + ')' }"
          :src="(preload || i === currentIndex) ? paintedUrl(item.url) : PLACEHOLDER"
          :alt="alt"
          :loading="i === 0 ? 'lazy' : 'eager'"
          decoding="async"
          @error="onError(item.angle)"
          @load="onImgLoad(item.angle)"
        >
      </div>
      <div v-if="showSpinner" class="spin-loading" aria-hidden="true"><i class="fas fa-circle-notch fa-spin"></i></div>
      <button type="button" class="spin-arrow spin-arrow-left" @click.stop.prevent="prev" aria-label="Előző szög">
        <i class="fas fa-chevron-left"></i>
      </button>
      <button type="button" class="spin-arrow spin-arrow-right" @click.stop.prevent="next" aria-label="Következő szög">
        <i class="fas fa-chevron-right"></i>
      </button>
    </template>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { Component, Prop, Watch } from 'vue-property-decorator';
  import { namespace } from 'vuex-class';

  const configuratorModule = namespace('configurator');

  const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  @Component
  export default class TypeSpinViewer extends Vue {
    @Prop({ type: Array, default: null }) angles;
    @Prop({ type: String, default: '' }) alt;
    @Prop({ type: String, default: '' }) defaultPaintId;
    @Prop({ type: Number, default: 1.5 }) zoom;

    @configuratorModule.Getter('getSelectedPaintId') selectedPaintId;

    currentIndex = 0;
    removedAngles = [];
    preload = false;
    appliedPaintId = null;
    paintLoading = false;
    loaded = {};

    // Drag state
    isDragging = false;
    dragStartX = 0;
    dragStartIndex = 0;
    wheelAccum = 0;

    created() {
      // Apply random default paint before first render (no flicker)
      if (this.defaultPaintId) {
        this.appliedPaintId = this.defaultPaintId;
      }
      // Set the initial displayed angle to the one whose angle===1 if present, else first
      if (Array.isArray(this.angles) && this.angles.length > 0) {
        const angle1Index = this.angles.findIndex(a => a.angle === 1);
        if (angle1Index !== -1) {
          this.currentIndex = angle1Index;
        }
      }
    }

    mounted() {
      this.$nextTick(() => { this.preload = true; });
    }

    get PLACEHOLDER() {
      return PLACEHOLDER;
    }

    get validAngles() {
      return Array.isArray(this.angles)
        ? this.angles.filter(a => !this.removedAngles.includes(a.angle))
        : [];
    }

    get hasViewer() {
      return Array.isArray(this.angles) && this.validAngles.length > 1;
    }

    get isLcv() {
      // Commercial vehicles render at zoom <= 1.0 (blade sets 1.0 for commercial segments) and
      // use zoomType=adaptive, which fills the 800x500 (1.6:1) canvas. Give them a 1.6:1 box so
      // the van fills it exactly with no side/bottom margin; cars keep the 2:1 box + 1.5x scale.
      return this.zoom <= 1.0;
    }

    get currentReady() {
      const cur = this.validAngles[this.currentIndex];
      return cur ? this.loaded[this.loadKey(cur.angle)] === true : true;
    }

    get showSpinner() {
      return this.hasViewer && (this.paintLoading || !this.currentReady);
    }

    loadKey(angle) {
      return angle + '|' + (this.appliedPaintId || '');
    }

    onImgLoad(angle) {
      this.$set(this.loaded, this.loadKey(angle), true);
    }

    paintedUrl(url) {
      return this.appliedPaintId ? url + '&paintId=' + encodeURIComponent(this.appliedPaintId) : url;
    }

    @Watch('selectedPaintId')
    onPaintChange(newId) {
      if (newId === this.appliedPaintId) return;
      const angles = this.validAngles;
      if (!angles.length) { this.appliedPaintId = newId; return; }
      const current = angles[this.currentIndex] || angles[0];

      // Build the recoloured URL for any angle item
      const urlFor = (item) => (newId ? item.url + '&paintId=' + encodeURIComponent(newId) : item.url);

      this.paintLoading = true;

      // 1) Preload EVERY angle in the new colour in parallel (warms Imagin's cold ~2.5-3s
      //    generation in the background so rotation after recolour stays instant — SCL parity).
      angles.forEach((item) => {
        if (item === current) return; // current handled below (it gates the swap)
        const pre = new Image();
        pre.src = urlFor(item);
      });

      // 2) The CURRENT angle gates the visible swap: keep the OLD frame + spinner until the
      //    new colour for the shown angle has decoded, then commit (instant, it's cached).
      const target = urlFor(current);
      const img = new Image();
      img.onload = () => {
        if (this.selectedPaintId === newId) {
          this.appliedPaintId = newId;
          // pre-mark the just-loaded current angle as ready under the new paint key so
          // currentReady stays true through the swap (no spinner flash after the swap)
          this.$set(this.loaded, current.angle + '|' + (newId || ''), true);
        }
        this.paintLoading = false;
      };
      img.onerror = () => { this.paintLoading = false; };
      img.src = target;
    }

    startPreload() {
      this.preload = true;
    }

    select(i) {
      this.startPreload();
      this.currentIndex = i;
    }

    prev() {
      if (!this.validAngles.length) return;
      const newIndex = (this.currentIndex - 1 + this.validAngles.length) % this.validAngles.length;
      this.select(newIndex);
    }

    next() {
      if (!this.validAngles.length) return;
      const newIndex = (this.currentIndex + 1) % this.validAngles.length;
      this.select(newIndex);
    }

    onError(angle) {
      const selectedAngle = (this.validAngles[this.currentIndex] || {}).angle;

      this.removedAngles = [...this.removedAngles, angle];

      const remaining = this.validAngles;
      const last      = Math.max(0, remaining.length - 1);
      const resolve   = (a, fallbackIdx) => {
        const idx = remaining.findIndex(x => x.angle === a);
        return idx === -1 ? Math.min(fallbackIdx, last) : idx;
      };

      this.currentIndex = resolve(selectedAngle, this.currentIndex);
    }

    // --- Pointer drag to rotate (hold to grab, move to switch angle, release to stop) ---
    // Pointer Events + setPointerCapture is the fix: capture routes every move/up to this
    // element even when the cursor leaves it, and preventDefault() kills the browser's native
    // image-drag (the ghost/selection that previously froze rotation mid-drag and left it
    // stuck because mouseup never fired on the element).
    onPointerDown(e) {
      if (e.button != null && e.button !== 0) return;                              // primary / touch only
      if (e.target && e.target.closest && e.target.closest('.spin-arrow')) return; // let chevrons click
      e.preventDefault();
      this.isDragging     = true;
      this.dragStartX     = e.clientX;
      this.dragStartIndex = this.currentIndex;
      this.startPreload();
      const el = e.currentTarget;
      if (e.pointerId != null && el && el.setPointerCapture) {
        try { el.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
    }

    onPointerMove(e) {
      if (!this.isDragging) return;
      const n = this.validAngles.length;
      if (!n) return;
      // Proportional, position-based: frame index tracks the cursor offset. Full-width drag
      // ≈ one full cycle through the available angles (with 5 frames it steps, not 360-smooth).
      const width      = (this.$el && this.$el.offsetWidth) || 600;
      const pxPerFrame = width / n;
      const offset     = Math.round((e.clientX - this.dragStartX) / pxPerFrame);
      if (offset === 0) return;
      const newIndex = ((this.dragStartIndex + offset) % n + n) % n;
      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
      }
    }

    onPointerUp(e) {
      this.isDragging = false;
      const el = e && e.currentTarget;
      if (e && e.pointerId != null && el && el.releasePointerCapture) {
        try { el.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
    }

    // Horizontal scroll (trackpad two-finger / shift-wheel) rotates the car; a vertical
    // scroll is left untouched so the page keeps scrolling over the viewer.
    onWheel(e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical → let the page scroll
      const n = this.validAngles.length;
      if (!n) return;
      e.preventDefault();
      this.startPreload();
      this.wheelAccum += e.deltaX;
      const STEP = 40; // px of horizontal scroll per angle step
      while (this.wheelAccum >= STEP) { this.next(); this.wheelAccum -= STEP; }
      while (this.wheelAccum <= -STEP) { this.prev(); this.wheelAccum += STEP; }
    }
  }
</script>

<style scoped>
  .type-spin-viewer {
    position: relative;
    overflow: hidden;
    width: 100%;
    cursor: grab;
    user-select: none;
    touch-action: pan-y;
    border-radius: 4px;
  }

  .type-spin-viewer:active {
    cursor: grabbing;
  }

  /* ratio box — reduced height so car fills more of the width */
  .type-spin-ratio {
    position: relative;
    width: 100%;
    padding-top: 50%;
    background: radial-gradient(120% 95% at 50% 22%, #fbfbfc 0%, #eef0f1 55%, #e4e6e8 100%);
  }

  /* LCV vans use zoomType=adaptive (fills the 1.6:1 image canvas) + groundPlaneAdjustment to
     seat the wheels on the turntable. A 1.6:1 box fits the image exactly — the van fills the full
     width with no side margin (the largest it can be without a scale-crop, which would clip a tall
     van's roof). Cars keep the 2:1 box and fill via their 1.5x scale. */
  .type-spin-ratio--lcv {
    padding-top: 62.5%;
  }

  /* Rotation hint: two curved arrows forming the turntable on the "floor" (sits behind the car) */
  .spin-rotate-hint {
    position: absolute;
    left: 50%;
    bottom: 6%;
    transform: translateX(-50%);
    width: 92%;
    max-width: 780px;
    height: auto;
    pointer-events: none;
    z-index: 0;
  }

  /* Fixed turntable width: sized to seat the widest car (large SUV side profile) on the
     ring; smaller cars sit generously inside. Same size at every angle (client request —
     the ring no longer resizes on rotate). */
  .type-spin-ratio:not(.type-spin-ratio--lcv) .spin-rotate-hint {
    width: 88%;
    max-width: none;
    bottom: 13%;
  }

  .type-spin-ratio img.type-spin-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .spin-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 6px 10px;
    cursor: pointer;
    z-index: 10;
    color: rgba(0, 0, 0, 0.55);
    transition: color 0.15s;
  }

  .spin-arrow:hover {
    color: rgba(0, 0, 0, 0.85);
  }

  .spin-arrow-left {
    left: 4px;
  }

  .spin-arrow-right {
    right: 4px;
  }

  .spin-arrow i {
    font-size: 34px;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.7);
  }

  .spin-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 12;
    pointer-events: none;
  }

  .spin-loading i {
    font-size: 28px;
    color: #6c757d;
  }
</style>
